// Copied from ../treasure-hunt/Editor/src/utils/flyCamera.ts (its spec 013, the
// fly-through view) for StoryMotion spec 025, the story studio's Fly-through
// map. A copy, not a shared package: the two may diverge from here.
// Changes: card kinds are `scene | end`; the route constants come from
// `storyRoute.ts` (which replaces the source's flyRoute.ts); the defaults are
// the studio's de-cluttered ones (025 D-12: tilt 0.9, distance 14, FOV 45°);
// `placeChoiceLabels` (025 D-14) keeps choice labels off the cards.
//
// The fly-through camera and its perspective projection. Pure.
// World: x sideways, y up, z along the route (layer × LAYER_DEPTH).
import { LAYER_DEPTH, spineX, spineDX } from './storyRoute';

/** The chase camera's state (targets and current values share this shape). */
export interface FlyCameraState {
  /** Position along the route in layers, [0, maxLayer]. */
  s: number;
  /** Sideways offset from the route in world units, [-8, 8]. */
  panX: number;
  /** Turn away from the route heading, rad. */
  yaw: number;
  /** Rad, [0.2, 1.4]. */
  pitch: number;
  /** World units, [4, 40]. */
  distance: number;
}

/** The top-down camera the switch starts from. */
export interface FlatCameraState {
  x: number;
  z: number;
  distance: number;
}

export interface Viewport {
  width: number;
  height: number;
}

/** A camera ready to project with. */
export interface Camera {
  /** Eased transition progress (0 flat, 1 chase). */
  e: number;
  /** Eye and target (world). */
  eye: { x: number; y: number; z: number };
  target: { x: number; y: number; z: number };
  heading: number;
  pitch: number;
  distance: number;
  /** Vertical field of view, rad. */
  fov: number;
  /** Focal length in pixels. */
  f: number;
  /** Projection centre on screen. */
  cx: number;
  cy: number;
  width: number;
  height: number;
  cp: number;
  sp: number;
  ch: number;
  sh: number;
}

export const PAN_LIMIT = 8;
export const PITCH_MIN = 0.2;
export const PITCH_MAX = 1.4;
/** De-cluttered (025 D-12): a steeper tilt spreads the rows apart on screen. */
export const DEFAULT_PITCH = 0.9;
export const DISTANCE_MIN = 4;
export const DISTANCE_MAX = 40;
export const DEFAULT_DISTANCE = 14;
/** A longer, narrower lens: near cards stop dwarfing far ones (025 D-12). */
export const FLY_FOV = (45 * Math.PI) / 180;
export const FLAT_FOV = (22 * Math.PI) / 180;
/** The camera aims this far (world units) past the current position. */
export const LEAD = 1.2;
/** Near plane depth. */
export const NEAR = 0.5;
/** Cards fade out between these depths. */
export const FOG_START = 26;
export const FOG_END = 52;
/** A near card is never larger than this. */
export const MAX_CARD_SCALE = 1.7;

export const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const smoothstep = (a: number, b: number, x: number) => {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
};
/** Cubic ease-in-out of the camera (named so 017's scan for keyframe easing does not take it for one). */
export const cameraEase = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/** The D-4 entry state: over the first stop, straight along the route. */
export const startCamera = (): FlyCameraState => ({
  s: 0,
  panX: 0,
  yaw: 0,
  pitch: DEFAULT_PITCH,
  distance: DEFAULT_DISTANCE,
});

/** Clamps every field of a fly state to its range. */
export const clampFly = (st: FlyCameraState, maxLayer: number): FlyCameraState => ({
  s: clamp(st.s, 0, Math.max(0, maxLayer)),
  panX: clamp(st.panX, -PAN_LIMIT, PAN_LIMIT),
  yaw: st.yaw,
  pitch: clamp(st.pitch, PITCH_MIN, PITCH_MAX),
  distance: clamp(st.distance, DISTANCE_MIN, DISTANCE_MAX),
});

/** The route heading at `s` plus the author's turn. */
export const flyHeading = (fly: Pick<FlyCameraState, 's' | 'yaw'>) =>
  Math.atan(spineDX(fly.s + 0.8) / LAYER_DEPTH) + fly.yaw;

/** The top-down view over the start of the story. */
export const flatCameraFor = (maxLayer: number): FlatCameraState => {
  const z = Math.min(13, (Math.max(1, maxLayer) * LAYER_DEPTH) / 2);
  return { x: spineX(z / LAYER_DEPTH), z, distance: 82 };
};

/** The camera for transition progress `t` in [0, 1] (0 flat, 1 chase), every parameter eased. */
export function cameraFor(
  fly: FlyCameraState,
  flat: FlatCameraState,
  t: number,
  viewport: Viewport
): Camera {
  const e = cameraEase(clamp(t, 0, 1));
  const fs = fly.s + LEAD / LAYER_DEPTH;
  const flyTarget = { x: spineX(fs) + fly.panX, z: fly.s * LAYER_DEPTH + LEAD };
  const fH = flyHeading(fly);
  // The flat view is the canvas orientation: a half turn from the chase view (D-3).
  const flatH = fH < 0 ? -Math.PI : Math.PI;
  const target = { x: lerp(flat.x, flyTarget.x, e), y: 0, z: lerp(flat.z, flyTarget.z, e) };
  const distance = Math.exp(lerp(Math.log(flat.distance), Math.log(fly.distance), e));
  const pitch = lerp(Math.PI / 2, fly.pitch, e);
  const heading = lerp(flatH, fH, e);
  const fov = lerp(FLAT_FOV, FLY_FOV, e);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const ch = Math.cos(heading);
  const sh = Math.sin(heading);
  const eye = {
    x: target.x - distance * sh * cp,
    y: distance * sp,
    z: target.z - distance * ch * cp,
  };
  const { width, height } = viewport;
  const f = height / 2 / Math.tan(fov / 2);
  return {
    e,
    eye,
    target,
    heading,
    pitch,
    distance,
    fov,
    f,
    cx: width / 2,
    cy: height * (0.5 + 0.1 * e),
    width,
    height,
    cp,
    sp,
    ch,
    sh,
  };
}

export type Vec3 = [number, number, number];

/** A world point in camera space: [right, up, depth]. */
export function toCamera(cam: Camera, p: { x: number; y: number; z: number }): Vec3 {
  const dx = p.x - cam.eye.x;
  const dy = p.y - cam.eye.y;
  const dz = p.z - cam.eye.z;
  const x1 = dx * cam.ch - dz * cam.sh;
  const z1 = dx * cam.sh + dz * cam.ch;
  // At pitch π/2 the "up" axis would degenerate; cp is never exactly 0 in practice.
  return [x1, dy * cam.cp + z1 * cam.sp, z1 * cam.cp - dy * cam.sp];
}

/** A camera-space point on screen. */
export const projectCamera = (cam: Camera, v: Vec3) => ({
  x: cam.cx + (cam.f * v[0]) / v[2],
  y: cam.cy - (cam.f * v[1]) / v[2],
  depth: v[2],
});

/** A world point on screen, with its depth (≤ 0 is behind the camera). */
export const project = (cam: Camera, p: { x: number; y: number; z: number }) =>
  projectCamera(cam, toCamera(cam, p));

/** Clips a camera-space segment against the near plane; null when entirely behind it. */
export function clipToNear(a: Vec3, b: Vec3): [Vec3, Vec3] | null {
  if (a[2] < NEAR && b[2] < NEAR) return null;
  if (a[2] >= NEAR && b[2] >= NEAR) return [a, b];
  const t = (NEAR - a[2]) / (b[2] - a[2]);
  const m: Vec3 = [lerp(a[0], b[0], t), lerp(a[1], b[1], t), NEAR];
  return a[2] < NEAR ? [m, b] : [a, m];
}

/** Fog: 1 up to depth 26, 0 by 52 (only once in the chase view). */
export const fogAlpha = (cam: Camera, depth: number) => 1 - cam.e * smoothstep(FOG_START, FOG_END, depth);

/** Height of a card's anchor above its footprint (world units, chase view). */
export type FlyCardKind = 'scene' | 'end';
export const STEM_HEIGHT: Record<FlyCardKind, number> = { scene: 1.15, end: 0.55 };
/** The cards' unscaled width in px (the studio's `SceneCard`, 025). */
const CARD_BASE_WIDTH: Record<FlyCardKind, number> = { scene: 200, end: 140 };
const CARD_WORLD_WIDTH: Record<FlyCardKind, number> = { scene: 2.5, end: 1.8 };

/** The scale of a card at `depth`, capped at 1.7. */
export const cardScale = (cam: Camera, depth: number, kind: FlyCardKind) => {
  const boost = lerp(2.3, 1, cam.e);
  return Math.min(MAX_CARD_SCALE, (cam.f * CARD_WORLD_WIDTH[kind] * boost) / depth / CARD_BASE_WIDTH[kind]);
};

export interface CardPlacement {
  visible: boolean;
  x: number;
  y: number;
  scale: number;
  opacity: number;
  depth: number;
  /** Vertical anchor shift in % of the card height (-50 flat, -100 chase). */
  anchorY: number;
}

/** Where a node's card goes: anchored by its bottom-centre above the footprint. */
export function cardPlacement(
  cam: Camera,
  node: { x: number; z: number; kind: FlyCardKind }
): CardPlacement {
  const v = toCamera(cam, { x: node.x, y: STEM_HEIGHT[node.kind] * cam.e, z: node.z });
  const hidden = { visible: false, x: 0, y: 0, scale: 0, opacity: 0, depth: v[2], anchorY: 0 };
  if (v[2] < NEAR + 0.4) return hidden;
  const p = projectCamera(cam, v);
  const scale = cardScale(cam, v[2], node.kind);
  const opacity =
    fogAlpha(cam, v[2]) * smoothstep(0.06, 0.14, scale) * (1 - cam.e * (1 - smoothstep(1.2, 3, v[2])));
  if (
    opacity < 0.02 ||
    p.x < -400 ||
    p.x > cam.width + 400 ||
    p.y < -300 ||
    p.y > cam.height + 600
  ) {
    return hidden;
  }
  return { visible: true, x: p.x, y: p.y, scale, opacity, depth: v[2], anchorY: lerp(-50, -100, cam.e) };
}

/** The box a card covers on screen (px): its rendered box, bottom-centred on its placement. */
export interface ScreenBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** A card's rendered height at scale 1 (px): the studio's scene and end cards. */
export const CARD_BASE_HEIGHT: Record<FlyCardKind, number> = { scene: 160, end: 100 };

/** The screen box of a placed card: `base × scale` wide and tall, bottom-centred on its point. */
export function cardBox(p: Pick<CardPlacement, 'x' | 'y' | 'scale'>, kind: FlyCardKind): ScreenBox {
  const w = CARD_BASE_WIDTH[kind] * p.scale;
  const h = CARD_BASE_HEIGHT[kind] * p.scale;
  return { left: p.x - w / 2, right: p.x + w / 2, top: p.y - h, bottom: p.y };
}

export const boxesOverlap = (a: ScreenBox, b: ScreenBox) =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/** A choice's text where the ground would draw it: its anchor, depth and font size. */
export interface ChoiceLabel {
  text: string;
  x: number;
  y: number;
  depth: number;
  fontSize: number;
}

/** Labels longer than this are cut (025 D-14). */
export const LABEL_MAX_CHARS = 28;

/** A label as drawn: longer than 28 characters, its first 27 and "…". */
export const trimChoiceLabel = (text: string) =>
  text.length > LABEL_MAX_CHARS ? `${text.slice(0, LABEL_MAX_CHARS - 1)}…` : text;

/**
 * The choice labels to draw (025 D-14), nearest first: each trimmed, and
 * dropped when its box (centred on its anchor, the font's size tall) overlaps
 * a card box or a label already kept. Pure.
 */
export function placeChoiceLabels(
  labels: readonly ChoiceLabel[],
  cardBoxes: readonly ScreenBox[],
  measure: (text: string, fontSize: number) => number,
): ChoiceLabel[] {
  const kept: ChoiceLabel[] = [];
  const keptBoxes: ScreenBox[] = [];
  const order = labels.map((l, i) => ({ l, i })).sort((a, b) => a.l.depth - b.l.depth || a.i - b.i);
  for (const { l } of order) {
    const text = trimChoiceLabel(l.text);
    const w = measure(text, l.fontSize);
    const box = { left: l.x - w / 2, right: l.x + w / 2, top: l.y - l.fontSize / 2, bottom: l.y + l.fontSize / 2 };
    if (cardBoxes.some((c) => boxesOverlap(box, c)) || keptBoxes.some((k) => boxesOverlap(box, k))) continue;
    kept.push({ ...l, text });
    keptBoxes.push(box);
  }
  return kept;
}
